/**
 * Phone numbers on the web — the twin of vc_be/utils/phone.js.
 *
 * A person's mobile is STORED in one shape, and the backend writes the same:
 *
 *   Indian number     → its 10 digits ("9876543210"), exactly what every
 *                       existing row holds
 *   any other country → E.164 with the "+" ("+14155552671")
 *
 * ⚠️ The "+" is load-bearing. Singapore's "+6591234567" is 10 digits without
 * it, and a bare 10-digit value is read as Indian everywhere — so it would be
 * dialled as +91 65912 34567.
 *
 * `PhoneInput` (components/ui) is the control. These are its rules, and a form
 * that validates a mobile itself asks `phoneProblem()`, never a regex of its own.
 */
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from "libphonenumber-js";
import metadata from "libphonenumber-js/metadata.min.json";

export const DEFAULT_PHONE_COUNTRY = "IN";
const INDIA_CALLING_CODE = "91";

/** Only digits, spaces, dots, dashes, brackets and "+": something typed as a number. */
const PHONE_CHARS = /^[\d\s+().-]+$/;

/** Offered first in the picker — where an institute's people abroad most often are. */
export const PINNED_PHONE_COUNTRIES = ["IN", "US", "GB", "AE", "CA", "AU", "SG"];

const digitsOnly = (value) => String(value ?? "").replace(/\D/g, "");

let countryList = null;

/** Every country the picker offers, sorted by name: `[{ code, callingCode, name }]`. */
export function phoneCountries() {
  if (countryList) return countryList;
  let names = null;
  try {
    names = new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    // A browser without Intl.DisplayNames shows the ISO code instead of the name.
  }
  countryList = getCountries()
    .map((code) => ({ code, callingCode: getCountryCallingCode(code), name: names?.of(code) || code }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return countryList;
}

/** "+91" for "IN". */
export function callingCodeOf(country) {
  try {
    return `+${getCountryCallingCode(country)}`;
  } catch {
    return "";
  }
}

/**
 * The country a parsed number belongs to. libphonenumber names the territory
 * ("GG" for some +44 mobiles); without one, the calling code's main country.
 */
function countryOf(parsed) {
  return parsed.country || metadata.country_calling_codes[parsed.countryCallingCode]?.[0] || "";
}

/** A number written with "+" (or "00") that is valid for its country, else null. */
function parseInternational(value) {
  const raw = String(value ?? "").trim();
  let candidate;
  if (raw.startsWith("+")) candidate = raw;
  else if (raw.startsWith("00")) candidate = `+${raw.slice(2)}`;
  else return null;
  try {
    const parsed = parsePhoneNumberFromString(candidate);
    return parsed?.isValid() ? parsed : null;
  } catch {
    return null;
  }
}

/** The stored shape of a parsed number: 10 digits for India, "+E.164" elsewhere. */
const storedForm = (parsed) =>
  parsed.countryCallingCode === INDIA_CALLING_CODE ? parsed.nationalNumber : parsed.number;

/**
 * The stored shape of what was typed into the number box with `country`
 * selected. A number typed with its own "+" (or "00") keeps that country,
 * whatever the picker says. Anything else that cannot be read comes back
 * as typed, and `phoneProblem` says what is wrong with it.
 *
 * @param {string} text
 * @param {string} [country="IN"] ISO 3166 code
 * @returns {string}
 */
export function canonicalPhone(text, country = DEFAULT_PHONE_COUNTRY) {
  const raw = String(text ?? "").trim();
  if (!raw || !PHONE_CHARS.test(raw)) return raw;

  const intl = parseInternational(raw);
  if (intl) return storedForm(intl);
  if (/^(\+|00)/.test(raw)) return raw;

  if (country === DEFAULT_PHONE_COUNTRY) {
    const digits = digitsOnly(raw);
    if (digits.length === 10) return digits;
    // An STD "0", or the country code typed without its "+".
    const trimmed = digits.replace(/^0+/, "");
    if (trimmed.length === 10) return trimmed;
    if (trimmed.length === 12 && trimmed.startsWith(INDIA_CALLING_CODE)) return trimmed.slice(2);
    return digits;
  }

  try {
    const parsed = parsePhoneNumberFromString(raw, country);
    if (parsed) return storedForm(parsed);
  } catch {
    // Falls through to the plain join below.
  }
  return `${callingCodeOf(country)}${digitsOnly(raw)}`;
}

/**
 * A stored mobile split for the picker: `{ country, national }`.
 *
 * Reads a stored value the way vc_be's `canonicalMobile` writes one, so older
 * spellings ("+91 98765 43210", an import's "14155552671") open on the right
 * country. A value it cannot read opens under `fallbackCountry` as it is, for
 * someone to correct.
 */
export function splitPhone(stored, fallbackCountry = DEFAULT_PHONE_COUNTRY) {
  const raw = String(stored ?? "").trim();
  if (!raw) return { country: fallbackCountry, national: "" };

  const fromParsed = (parsed) => ({ country: countryOf(parsed), national: parsed.nationalNumber });

  const intl = parseInternational(raw);
  if (intl) return fromParsed(intl);

  if (PHONE_CHARS.test(raw) && !/^(\+|00)/.test(raw)) {
    const digits = digitsOnly(raw);
    if (digits.length === 10) return { country: DEFAULT_PHONE_COUNTRY, national: digits };
    const trimmed = digits.replace(/^0+/, "");
    if (trimmed.length === 10) return { country: DEFAULT_PHONE_COUNTRY, national: trimmed };
    if (trimmed.length === 12 && trimmed.startsWith(INDIA_CALLING_CODE)) {
      return { country: DEFAULT_PHONE_COUNTRY, national: trimmed.slice(2) };
    }
    const withPlus = parseInternational(`+${trimmed}`);
    if (withPlus) return fromParsed(withPlus);
  }

  // An invalid "+" number still opens on its own calling code where it has one.
  if (raw.startsWith("+")) {
    try {
      const parsed = parsePhoneNumberFromString(raw);
      if (parsed && countryOf(parsed)) return fromParsed(parsed);
    } catch {
      // Falls through.
    }
  }
  return { country: fallbackCountry, national: raw };
}

/** The country a value typed with its own "+" names, or null — for moving the picker to it. */
export function countryOfTyped(text) {
  const parsed = parseInternational(text);
  return parsed ? { country: countryOf(parsed), national: parsed.nationalNumber } : null;
}

/**
 * What is wrong with a stored-shape mobile, as a sentence for the person who
 * typed it — or null when it is fine. Empty is fine here; "required" is the
 * field's business.
 *
 * India keeps the rule every form has always applied: exactly 10 digits.
 */
export function phoneProblem(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  if (/^\d{10}$/.test(raw)) return null;
  if (!raw.startsWith("+")) return "Enter a 10-digit mobile number.";

  try {
    const parsed = parsePhoneNumberFromString(raw);
    if (parsed?.isValid()) return null;
    const country = parsed && phoneCountries().find((c) => c.code === countryOf(parsed));
    const where = country ? ` for ${country.name}` : "";
    const length = validatePhoneNumberLength(raw);
    if (length === "TOO_SHORT") return `This number is too short${where}.`;
    if (length === "TOO_LONG") return `This number is too long${where}.`;
    if (length === "INVALID_COUNTRY") return "That country code is not recognised.";
    return `Enter a valid mobile number${where}.`;
  } catch {
    return "Enter a valid mobile number.";
  }
}
